import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// Standalone prospect demo site — intentionally zero TraceElements/Emergent branding.
export default function DemoSite() {
  const { slug } = useParams();
  const [demo, setDemo] = useState(null);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/demo/${slug}`)
      .then((r) => { if (alive) { setDemo(r.data); document.title = r.data.client_name; } })
      .catch(() => { if (alive) setMissing(true); });
    return () => { alive = false; };
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/demo/${slug}/inquiry`, form);
      setSent(true);
    } catch {
      alert("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14] text-[#F2F3F5]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="demo-not-found">
        <div className="text-center px-6">
          <div className="text-6xl font-extrabold tracking-tighter opacity-20">404</div>
          <p className="mt-4 text-sm opacity-60">This page isn't available.</p>
        </div>
      </div>
    );
  }

  if (!demo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14]" data-testid="demo-loading">
        <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white/80 animate-spin" />
      </div>
    );
  }

  const dark = demo.theme !== "light";
  const bg = dark ? "#0B0E14" : "#FAFAF8";
  const text = dark ? "#F2F3F5" : "#16181D";
  const muted = dark ? "rgba(242,243,245,0.65)" : "rgba(22,24,29,0.62)";
  const cardBg = dark ? "#11151D" : "#FFFFFF";
  const border = dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)";
  const primary = demo.primary_color || "#2E6BFF";
  const accent = demo.accent_color || "#10B981";

  const services = [...(demo.services || [])].slice(0, 3);
  while (services.length < 3) services.push({ title: `Service ${services.length + 1}`, description: "" });

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg, color: text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="demo-page">
      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: dark ? "rgba(11,14,20,0.85)" : "rgba(250,250,248,0.88)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${border}` }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-extrabold text-lg tracking-tight" data-testid="demo-brand">{demo.client_name}</span>
          <nav className="flex items-center gap-6 text-sm font-semibold">
            <a href="#about" className="hidden sm:inline hover:opacity-100 opacity-70 transition-opacity">About</a>
            <a href="#services" className="hidden sm:inline hover:opacity-100 opacity-70 transition-opacity">Services</a>
            <a href="#contact" className="px-5 py-2.5 font-bold text-white text-xs uppercase tracking-widest" style={{ backgroundColor: primary, borderRadius: 999 }} data-testid="demo-nav-cta">
              {demo.cta_text}
            </a>
          </nav>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="demo-hero">
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(55% 60% at 75% 15%, ${primary}30, transparent 70%), radial-gradient(40% 45% at 15% 85%, ${accent}24, transparent 70%)` }} />
        <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-36">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] max-w-3xl" data-testid="demo-hero-headline">
            {demo.headline || demo.client_name}
          </h1>
          {demo.subheadline && (
            <p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: muted }} data-testid="demo-hero-subheadline">{demo.subheadline}</p>
          )}
          <a
            href="#contact"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: primary, borderRadius: 999, boxShadow: `0 12px 40px ${primary}55` }}
            data-testid="demo-hero-cta"
          >
            {demo.cta_text} <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="py-20 lg:py-24" style={{ borderTop: `1px solid ${border}` }} data-testid="demo-about">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-xs font-extrabold uppercase tracking-[0.3em]" style={{ color: accent }}>Who We Are</div>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight">{demo.about_title}</h2>
          </div>
          <p className="lg:col-span-8 text-base lg:text-lg leading-relaxed" style={{ color: muted }} data-testid="demo-about-text">{demo.about_text}</p>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-20 lg:py-24" style={{ borderTop: `1px solid ${border}` }} data-testid="demo-services">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs font-extrabold uppercase tracking-[0.3em]" style={{ color: accent }}>What We Do</div>
          <h2 className="mt-4 text-3xl lg:text-4xl font-extrabold tracking-tight">Services</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <div
                key={i}
                className="p-8 transition-transform duration-300 hover:-translate-y-1"
                style={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: 20 }}
                data-testid={`demo-service-${i}`}
              >
                <div className="text-4xl font-extrabold tracking-tighter" style={{ color: `${primary}59` }}>0{i + 1}</div>
                <h3 className="mt-4 text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: muted }}>{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-20 lg:py-24" style={{ borderTop: `1px solid ${border}` }} data-testid="demo-contact">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-extrabold uppercase tracking-[0.3em]" style={{ color: accent }}>Get In Touch</div>
          <h2 className="mt-4 text-3xl lg:text-4xl font-extrabold tracking-tight">Let's talk.</h2>
          {sent ? (
            <div
              className="mt-10 inline-flex items-center gap-3 px-8 py-6 text-base font-bold"
              style={{ backgroundColor: `${accent}1f`, border: `1px solid ${accent}66`, borderRadius: 16, color: accent }}
              data-testid="demo-contact-success"
            >
              <CheckCircle2 size={20} /> Thanks — we'll be in touch shortly.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4 text-left" data-testid="demo-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="demo-contact-name"
                  className="w-full px-5 py-4 text-sm outline-none"
                  style={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: 12, color: text }}
                />
                <input
                  required type="email" placeholder="Email address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="demo-contact-email"
                  className="w-full px-5 py-4 text-sm outline-none"
                  style={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: 12, color: text }}
                />
              </div>
              <textarea
                required rows={5} placeholder="How can we help?" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="demo-contact-message"
                className="w-full px-5 py-4 text-sm outline-none resize-none"
                style={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: 12, color: text }}
              />
              <button
                type="submit" disabled={busy}
                className="w-full py-4 text-sm font-extrabold uppercase tracking-widest text-white disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: primary, borderRadius: 999, boxShadow: `0 10px 30px ${primary}44` }}
                data-testid="demo-contact-submit"
              >
                {busy ? "Sending…" : demo.cta_text}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ===== FOOTER (client branding only) ===== */}
      <footer className="py-8" style={{ borderTop: `1px solid ${border}` }} data-testid="demo-footer">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-extrabold tracking-tight">{demo.client_name}</span>
          <span className="text-xs" style={{ color: muted }}>© {new Date().getFullYear()} {demo.client_name}. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
